import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus, MessageCircle, ShoppingBag, Ruler } from "lucide-react";
import { TIER_LABEL, SIZES, DEFAULT_SIZE } from "@/data/products";
import { brl } from "@/lib/format";
import { waLink, buildSingleOrderMessage } from "@/lib/whatsapp";
import { trackWhatsAppClick } from "@/lib/analytics";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { PRODUCT } from "@/constants/testIds";

export const ProductDialog = ({ product, onClose }) => {
  const [sizeId, setSizeId] = useState(DEFAULT_SIZE);
  const [qty, setQty] = useState(1);
  const [faixa, setFaixa] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    if (product) {
      setSizeId(DEFAULT_SIZE);
      setQty(1);
      setFaixa("");
      setGuideOpen(false);
    }
  }, [product]);

  if (!product) return null;

  const size = SIZES.find((s) => s.id === sizeId);
  const unitPrice = product.sizes[sizeId];
  const total = unitPrice * qty;

  const handleAddToCart = () => {
    addItem({ product, size, price: unitPrice, quantity: qty, faixa });
    toast.success("Item adicionado ao pedido", { description: `${product.name} · ${size.label} ${size.dim} × ${qty}` });
    onClose();
    setTimeout(() => setIsOpen(true), 200);
  };

  const handleBuyNow = () => {
    trackWhatsAppClick("product_dialog", { ref: product.ref, price: total, size: size.label });
    const msg = buildSingleOrderMessage({ product, size, price: unitPrice, faixa, quantity: qty });
    window.open(waLink(msg), "_blank");
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={PRODUCT.dialog}
        className="max-w-4xl p-0 overflow-hidden bg-white border-[#E5E3DB] gap-0"
      >
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square md:aspect-auto bg-[#F4F2EC]">
            <img src={product.img} alt={`Coroa de flores ${product.ref} para velório · Central de Coroa de Flores RJ`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute top-4 left-4 bg-white/90 backdrop-blur text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full text-[#2D4A3E]">
              {TIER_LABEL[product.tier]}
            </span>
          </div>

          <div className="p-7 md:p-9 overflow-y-auto max-h-[85vh]">
            <span className="overline">{product.ref}</span>
            <DialogTitle className="text-3xl md:text-4xl font-semibold text-[#2D4A3E] mt-2 leading-tight tracking-tight">
              {product.name}
            </DialogTitle>
            <DialogDescription className="text-[#5C5C5C] mt-3 text-sm leading-relaxed">
              {product.description}
            </DialogDescription>

            <div className="mt-6">
              <div className="flex items-center justify-between">
                <Label className="text-xs tracking-[0.2em] uppercase text-[#BFA573]">Tamanho</Label>
                <button
                  type="button"
                  onClick={() => setGuideOpen((v) => !v)}
                  data-testid="size-guide-toggle"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2D4A3E] hover:text-[#1F352B]"
                >
                  <Ruler className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {guideOpen ? "Ocultar comparação" : "Ver comparação visual"}
                </button>
              </div>

              {guideOpen && (
                <button
                  type="button"
                  onClick={() => window.open("/hero/tamanhos.webp", "_blank")}
                  data-testid="size-guide-image"
                  className="mt-3 block w-full rounded-lg overflow-hidden border border-[#E5E3DB] bg-white hover:border-[#2D4A3E] transition-colors"
                  aria-label="Comparação visual de tamanhos"
                >
                  <img
                    src="/hero/tamanhos.webp"
                    alt="Comparação de tamanhos de coroa de flores: P 1,00m, M 1,20m, G 1,40m e GG 1,60m"
                    className="w-full h-auto"
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#5C5C5C] py-2 text-center">
                    Toque para ampliar · Referência de altura (pessoa 1,75m)
                  </p>
                </button>
              )}

              <div className="mt-3 grid grid-cols-4 gap-2">
                {SIZES.map((s) => {
                  const active = s.id === sizeId;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSizeId(s.id)}
                      data-testid={`dialog-size-${s.id}`}
                      className={`py-3 rounded-lg text-sm border transition-all ${
                        active
                          ? "bg-[#2D4A3E] text-white border-[#2D4A3E]"
                          : "bg-white text-[#1C1C1C] border-[#E5E3DB] hover:border-[#2D4A3E]"
                      }`}
                    >
                      <div className="font-medium">{s.label}</div>
                      <div className={`text-[11px] mt-0.5 ${active ? "text-white/85" : "text-[#5C5C5C]"}`}>
                        {s.dim}
                      </div>
                      <div className={`text-[11px] mt-1 tabular-nums ${active ? "text-white/85" : "text-[#5C5C5C]"}`}>
                        {brl(product.sizes[s.id])}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 pb-6 border-b border-[#E5E3DB]">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573]">Valor unitário · {size.label} {size.dim}</p>
              <p className="font-semibold text-[36px] leading-none text-[#2D4A3E] mt-2 tabular-nums tracking-tight">
                {brl(unitPrice)}
              </p>
            </div>

            <div className="mt-6">
              <Label htmlFor="faixa" className="text-xs tracking-[0.2em] uppercase text-[#BFA573]">
                Faixa personalizada (opcional)
              </Label>
              <Textarea
                id="faixa"
                data-testid={PRODUCT.faixaInput}
                value={faixa}
                onChange={(e) => setFaixa(e.target.value)}
                placeholder='Ex: "Saudades eternas — sua família"'
                maxLength={140}
                rows={2}
                className="mt-2 border-[#E5E3DB] focus-visible:ring-[#2D4A3E] resize-none"
              />
              <p className="mt-1 text-xs text-[#5C5C5C]">Mensagem impressa na faixa de homenagem (sem custo).</p>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Diminuir"
                        className="h-9 w-9 rounded-full border border-[#E5E3DB] hover:border-[#2D4A3E] flex items-center justify-center">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="font-medium w-6 text-center">{qty}</span>
                <button onClick={() => setQty(qty + 1)} aria-label="Aumentar"
                        className="h-9 w-9 rounded-full border border-[#E5E3DB] hover:border-[#2D4A3E] flex items-center justify-center">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="text-right">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#BFA573]">Total</p>
                <p className="font-semibold text-[30px] leading-none text-[#2D4A3E] mt-1 tabular-nums tracking-tight">{brl(total)}</p>
              </div>
            </div>

            <div className="mt-7 flex flex-col gap-2.5">
              <button data-testid={PRODUCT.buyNow} onClick={handleBuyNow} className="btn-whatsapp w-full">
                <MessageCircle className="h-4 w-4" /> Pedir agora pelo WhatsApp
              </button>
              <button data-testid={PRODUCT.addCart} onClick={handleAddToCart} className="btn-outline w-full">
                <ShoppingBag className="h-4 w-4" /> Adicionar ao pedido
              </button>
            </div>

            <p className="mt-4 text-xs text-[#5C5C5C] text-center">
              Resposta imediata · Atendimento 24h · Entrega grátis no RJ
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
